import { useMutation } from "@tanstack/react-query";
import { actions } from "astro:actions";
import { navigate } from "astro:transitions/client";
import { $path } from "astro-typesafe-routes/path";
import { shuffle } from "lodash-es";
import { useState } from "react";

import { DialogBody } from "~/components/DialogBody";
import { DialogFooter } from "~/components/DialogFooter";
import { DialogHeader } from "~/components/DialogHeader";
import { DialogTitle } from "~/components/DialogTitle";
import { QuotaAlert } from "~/components/QuotaAlert";
import { Textarea } from "~/components/Textarea";
import { useQuota } from "~/hooks/quota";
import { MAX_PROMPT_LENGTH } from "~/utils/const";
import { slugify } from "~/utils/slug";

export const GeneratePack: React.FC = () => {
  const quota = useQuota();
  const [ideas] = useState(() => shuffle(prompts).slice(0, 6));
  const [prompt, setPrompt] = useState("");

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: actions.ai.generatePack.orThrow,
    onSuccess: (pack) => {
      navigate(
        $path({
          to: "/account/packs/[slug]",
          params: {
            slug: slugify(pack),
          },
        }),
      );
    },
  });

  return (
    <form
      className="contents"
      onSubmit={(event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        mutate(formData);
      }}
    >
      <DialogHeader>Generate a pack</DialogHeader>
      <DialogBody className="space-y-4">
        <div className="space-y-2">
          <DialogTitle>What do you want to learn about?</DialogTitle>
          <p>
            Describe a topic, and we&apos;ll generate a pack with words and
            clues based on your prompt. Be as specific as possible—mention grade
            level, reading difficulty, or writing style to get the best results.
          </p>
        </div>
        <QuotaAlert quota={quota.packs} type="pack" />
        <Textarea
          required
          name="prompt"
          label="Prompt"
          placeholder="Describe your topic, and specify grade level, writing style, etc."
          maxLength={MAX_PROMPT_LENGTH}
          value={prompt}
          onChange={(event) => {
            setPrompt(event.target.value);
          }}
        />
        <div className="flex flex-wrap gap-2">
          {ideas.map((idea, index) => (
            <div
              className="btn btn-outline btn-sm"
              key={index}
              onClick={() => {
                setPrompt(idea);
              }}
            >
              {idea}
            </div>
          ))}
        </div>
      </DialogBody>
      <DialogFooter>
        <button
          type="submit"
          disabled={!quota.packs || isPending || isSuccess}
          className="btn btn-secondary btn-block"
        >
          {isPending ? (
            <>
              <span className="loading loading-dots loading-sm" />
              Generating pack...
            </>
          ) : (
            "Generate pack"
          )}
        </button>
      </DialogFooter>
    </form>
  );
};

const prompts = [
  // 📚 general vocabulary
  "SAT words",
  "Commonly confused words",
  "Advanced English words",
  "Basic sight words",
  "Fun & quirky words",
  "Words with silent letters",
  "Rhyming words",
  "Words with multiple meanings",
  "Borrowed words",
  "Common idioms",

  // 🔬 science & nature
  "Planets & space",
  "Physics concepts",
  "Biology terms",
  "Animal adaptations",
  "Human body parts",
  "Periodic table elements",
  "Types of clouds",
  "Ecosystem terms",
  "Genetics vocabulary",
  "Famous scientists",

  // 🌎 geography & places
  "World capitals",
  "U.S. states & capitals",
  "Countries & flags",
  "Landforms & terrains",
  "Oceans & seas",
  "Famous landmarks",
  "National parks",
  "Continents & regions",
  "Rivers & lakes",
  "Historic cities",

  // 🎭 arts & entertainment
  "Famous paintings",
  "Classical composers",
  "Movie genres",
  "Oscar-winning films",
  "Broadway musicals",
  "Mythical creatures",
  "Popular video games",
  "Fairy tale characters",
  "Famous book titles",
  "Comic book heroes",

  // 🏛️ history & politics
  "U.S. presidents",
  "Ancient civilizations",
  "Revolutions & wars",
  "Famous speeches",
  "World leaders",
  "Greek & Roman mythology",
  "Medieval times",
  "The Renaissance",
  "Inventions & inventors",
  "Cold War events",

  // 💡 everyday knowledge
  "Kitchen tools",
  "Sports equipment",
  "Common phobias",
  "Internet slang",
  "Medical terms",
  "Fashion trends",
  "Famous brands",
  "Popular pets",
  "Board games",
  "Car brands",

  // 🎵 music & pop culture
  "Beatles songs",
  "Hip-hop slang",
  "Rock legends",
  "Famous album titles",
  "K-pop groups",
  "80s pop hits",
  "TikTok trends",
  "Super Bowl halftime acts",
  "Grammy-winning artists",
  "Disney songs",

  // 🏆 sports & games
  "Olympic sports",
  "NFL teams",
  "NBA legends",
  "Soccer terms",
  "Chess moves",
  "Tennis champions",
  "Video game characters",
  "Esports teams",
  "Extreme sports",
  "Fantasy football terms",

  // 🏠 home & lifestyle
  "DIY tools",
  "Gardening terms",
  "Interior design styles",
  "Cooking techniques",
  "Home appliances",
  "Famous chefs",
  "Popular diets",
  "Camping essentials",
  "Coffee varieties",
  "Tea types",
];
